// ---------------------------------------------------------------------------
// skein-handler.ts — browser-side handler for incoming skein/1 ALPN streams
//
// when a peer (tauri or browser) opens a skein/1 stream to us,
// this handler reads the request, dispatches to the appropriate
// local handler (blob data from OPFS, metadata from IndexedDB),
// and writes back a response.
//
// this enables browser peers to serve their uploaded/snatched files
// to other peers on the canvas.
//
// protocol framing: grimoire and midden both use RAW JSON (no length prefix)
// for the skein/1 protocol. messages are terminated by the sender calling
// finish() on the send stream, and the receiver reads with read_to_end().
// this is NOT the length-delimited framing used by BiStream.read_message().
// ---------------------------------------------------------------------------

import type { BiStreamLike } from "./iroh-network-adapter";
import { base64Encode } from "@freqhole/reliquary/worker";
import * as blobStore from "../storage/blob-store";
import { getBlobDomain } from "../storage/blob-store";
import { getMiddenNode as getMiddenNodeFromIdentity } from "./identity";
import { isTauriMode, dispatch } from "./tauri-transport";
import { log } from "@freqhole/reliquary/utils";
import { createEnsureBlobHandler } from "@freqhole/reliquary/ensure";

// ---- peer message types ---------------------------------------------------
// these match midden's PeerMessage enum (serde tag = "type", rename_all = "snake_case")

interface ProxyRequest {
  type: "proxy_request";
  id: number;
  method: string;
  path: string;
  body?: string | null;
}

interface ProxyResponse {
  type: "proxy_response";
  id: number;
  status: number;
  body: string;
}

interface ComputeBlake3Request {
  type: "compute_blake3_request";
  id: number;
  blob_id: string;
}

interface ComputeBlake3Response {
  type: "compute_blake3_response";
  id: number;
  blake3: string | null;
  error?: string | null;
}

type PeerMessage =
  | ProxyRequest
  | ProxyResponse
  | ComputeBlake3Request
  | ComputeBlake3Response
  | { type: string; [key: string]: unknown };

// ---- constants ------------------------------------------------------------

const TAG = "skein.handler";

// ---- helpers --------------------------------------------------------------

const encoder = new TextEncoder();
const decoder = new TextDecoder();

/**
 * send a response as raw JSON + finish the stream (no length prefix).
 * matches grimoire's send_response() / recv.read_to_end() framing.
 * used for proxy_response messages where the receiver reads until stream end.
 */
async function sendRawResponse(stream: BiStreamLike, msg: PeerMessage): Promise<void> {
  const json = JSON.stringify(msg);
  const bytes = encoder.encode(json);
  if (stream.write_raw_and_finish) {
    await stream.write_raw_and_finish(bytes);
  } else {
    // fallback for streams that don't support raw write
    await stream.write_message(bytes);
  }
}

// base64 encoding is delegated to the blob worker (off-main-thread).
// `data` ownership is transferred to the worker — do not reuse after.
async function arrayBufferToBase64(buffer: ArrayBuffer): Promise<string> {
  return base64Encode(buffer);
}

// ---- proxy request dispatch -----------------------------------------------

/**
 * extract a path parameter from a URL pattern.
 * e.g. extractPathParam("/api/blobs/abc123/data", /^\/api\/blobs\/([^/]+)\/data$/) => "abc123"
 */
function extractPathParam(path: string, pattern: RegExp): string | null {
  const match = path.match(pattern);
  return match ? match[1] : null;
}

async function handleProxyRequest(stream: BiStreamLike, msg: ProxyRequest): Promise<void> {
  const { id, method, path, body } = msg;
  const peerId = stream.peer_node_id().slice(0, 16);

  log.debug(TAG, `proxy ${method} ${path} from ${peerId}...`);

  let parsedBody: Record<string, unknown> = {};
  if (body) {
    try {
      parsedBody = JSON.parse(body);
    } catch {
      // body is not JSON — that's fine for some endpoints
    }
  }

  try {
    // POST /api/blobs/thumbnail_data
    if (path === "/api/blobs/thumbnail_data" && method === "POST") {
      await handleThumbnailData(stream, id, parsedBody);
      return;
    }

    // GET /api/blobs/{id}/data
    const blobDataId = extractPathParam(path, /^\/api\/blobs\/([^/]+)\/data$/);
    if (blobDataId && method === "GET") {
      await handleBlobData(stream, id, blobDataId);
      return;
    }

    // POST or GET /api/blob_metadata
    if (
      (path === "/api/blob_metadata" || path === "/api/blobs/metadata") &&
      (method === "POST" || method === "GET")
    ) {
      await handleBlobMetadata(stream, id, parsedBody);
      return;
    }

    // fallback — not implemented
    log.debug(TAG, `unhandled proxy route: ${method} ${path}`);
    await sendRawResponse(stream, {
      type: "proxy_response",
      id,
      status: 404,
      body: JSON.stringify({ success: false, message: "not implemented" }),
    });
  } catch (err) {
    log.error(TAG, `error handling proxy ${method} ${path}:`, err);
    await sendRawResponse(stream, {
      type: "proxy_response",
      id,
      status: 500,
      body: JSON.stringify({
        success: false,
        message: err instanceof Error ? err.message : "internal error",
      }),
    });
  }
}

async function handleThumbnailData(
  stream: BiStreamLike,
  id: number,
  body: Record<string, unknown>
): Promise<void> {
  const blobId = (body.blob_id ?? body.id) as string | undefined;
  if (!blobId) {
    await sendRawResponse(stream, {
      type: "proxy_response",
      id,
      status: 400,
      body: JSON.stringify({ success: false, message: "missing blob_id" }),
    });
    return;
  }

  const store = blobStore;
  const record = await store.resolveBlob(blobId);
  if (!record) {
    await sendRawResponse(stream, {
      type: "proxy_response",
      id,
      status: 404,
      body: JSON.stringify({ success: false, message: "blob not found" }),
    });
    return;
  }

  const data = await store.getBlobData(record.blob_id);
  if (!data) {
    await sendRawResponse(stream, {
      type: "proxy_response",
      id,
      status: 404,
      body: JSON.stringify({ success: false, message: "blob data not found in OPFS" }),
    });
    return;
  }

  // browser doesn't generate thumbnails — serve the original image data.
  // this is fine for photos; the peer will display the original.
  const base64 = await arrayBufferToBase64(data);
  await sendRawResponse(stream, {
    type: "proxy_response",
    id,
    status: 200,
    body: JSON.stringify({
      success: true,
      data: {
        data: base64,
        mime: record.mime || "application/octet-stream",
      },
    }),
  });
}

async function handleBlobData(stream: BiStreamLike, id: number, blobId: string): Promise<void> {
  const store = blobStore;
  const record = await store.resolveBlob(blobId);
  if (!record) {
    await sendRawResponse(stream, {
      type: "proxy_response",
      id,
      status: 404,
      body: JSON.stringify({ success: false, message: "blob not found" }),
    });
    return;
  }

  const data = await store.getBlobData(record.blob_id);
  if (!data) {
    await sendRawResponse(stream, {
      type: "proxy_response",
      id,
      status: 404,
      body: JSON.stringify({ success: false, message: "blob data not found in OPFS" }),
    });
    return;
  }

  const base64 = await arrayBufferToBase64(data);
  await sendRawResponse(stream, {
    type: "proxy_response",
    id,
    status: 200,
    body: JSON.stringify({
      success: true,
      data: {
        data: base64,
        mime: record.mime || "application/octet-stream",
      },
    }),
  });
}

async function handleBlobMetadata(
  stream: BiStreamLike,
  id: number,
  body: Record<string, unknown>
): Promise<void> {
  const blobId = (body.id ?? body.blob_id) as string | undefined;
  if (!blobId) {
    await sendRawResponse(stream, {
      type: "proxy_response",
      id,
      status: 400,
      body: JSON.stringify({ success: false, message: "missing id" }),
    });
    return;
  }

  const store = blobStore;
  const record = await store.resolveBlob(blobId);
  if (!record) {
    await sendRawResponse(stream, {
      type: "proxy_response",
      id,
      status: 404,
      body: JSON.stringify({ success: false, message: "blob not found" }),
    });
    return;
  }

  // return a simplified record matching what grimoire returns
  await sendRawResponse(stream, {
    type: "proxy_response",
    id,
    status: 200,
    body: JSON.stringify({
      success: true,
      data: {
        id: record.blob_id,
        mime: record.mime,
        filename: record.filename,
        size: record.size,
        blake3: record.blake3 || null,
        domain: getBlobDomain(record),
        blob_type: record.blob_type,
      },
    }),
  });
}

// ---- compute_blake3 request -----------------------------------------------

/** blobs at or above this size are imported into the iroh-blobs store via
 *  midden's chunked ImportSession (add_stream) instead of a whole-buffer
 *  import_blob call — the wasm boundary never sees the full payload. below
 *  it, the bao-caching whole-buffer path stays (fast re-import for modest
 *  files, and the bao export is a whole-buffer operation anyway). */
const STREAM_IMPORT_THRESHOLD = 8 * 1024 * 1024;

/**
 * chunk-stream a blob's OPFS bytes into the node's iroh-blobs store via
 * ImportSession. `file` is a lazily-read File handle (no bytes loaded
 * until streamed). returns the blake3 hash reported by the store.
 */
async function importBlobStreaming(node: any, file: File): Promise<string> {
  const session = node.start_import();
  const reader = file.stream().getReader();
  try {
    for (;;) {
      const { done, value } = await reader.read();
      if (done) break;
      await session.push(value);
    }
    return (await session.finish()) as string;
  } catch (err) {
    try {
      session.abort();
    } catch {
      // already finished/aborted
    }
    throw err;
  } finally {
    reader.releaseLock();
  }
}

async function handleComputeBlake3(stream: BiStreamLike, msg: ComputeBlake3Request): Promise<void> {
  const { id, blob_id } = msg;
  const peerId = stream.peer_node_id().slice(0, 16);

  log.debug(TAG, `compute_blake3 for ${blob_id.slice(0, 8)}... from ${peerId}...`);

  const store = blobStore;

  // streaming path for large blobs: resolve the record, then chunk-stream
  // the OPFS file into the store — no whole-buffer copy on either side.
  try {
    const record = await store.resolveBlob(blob_id);
    if (record) {
      const node = await getMiddenNodeFromIdentity();
      if (
        typeof (node as any).start_import === "function" &&
        (record.size ?? 0) >= STREAM_IMPORT_THRESHOLD
      ) {
        const file = await store.getBlobFile(record.blob_id);
        if (file) {
          const hash = await importBlobStreaming(node, file);
          log.debug(
            TAG,
            `computed blake3 for ${blob_id.slice(0, 8)}... via streaming import: ${hash.slice(0, 16)}...`
          );
          await sendRawResponse(stream, { type: "compute_blake3_response", id, blake3: hash });
          return;
        }
      }
    }
  } catch (err) {
    log.warn(TAG, "streaming compute_blake3 failed, falling back to buffered path:", err);
  }

  const resolved = await store.resolveBlobData(blob_id);

  if (!resolved) {
    await sendRawResponse(stream, {
      type: "compute_blake3_response",
      id,
      blake3: null,
      error: "blob not found in OPFS",
    });
    return;
  }

  let data: ArrayBuffer | null = resolved.data;

  try {
    // import into MemStore AND export the bao-encoded bytes for caching.
    // import_blob_and_export_bao returns { hash, bao } — we use its hash
    // as the blake3 result instead of calling hash_blake3 separately.
    // this avoids copying the full blob into WASM twice (OOM fix).
    const node = await getMiddenNodeFromIdentity();
    let hash: string;

    if (typeof (node as any).import_blob_and_export_bao === "function") {
      try {
        const result = await (node as any).import_blob_and_export_bao(new Uint8Array(data));
        // release the JS ArrayBuffer to allow GC before caching bao
        data = null;
        hash = result.hash;
        if (result && result.bao) {
          const baoStore = blobStore;
          await baoStore.storeBaoData(hash, result.bao.buffer);
          log.debug(
            TAG,
            `cached bao data for ${hash.slice(0, 16)}... (${result.bao.byteLength} bytes)`
          );
        }
      } catch (baoErr) {
        // fallback to plain import if bao export fails — import_blob also returns the hash
        log.warn(TAG, `bao export failed, falling back to plain import:`, baoErr);
        hash = await (node as any).import_blob(new Uint8Array(data!));
        data = null;
      }
    } else {
      // midden build without bao export support — import_blob returns the hash
      hash = await (node as any).import_blob(new Uint8Array(data));
      data = null;
    }

    log.debug(TAG, `computed blake3 for ${blob_id.slice(0, 8)}...: ${hash.slice(0, 16)}...`);

    await sendRawResponse(stream, {
      type: "compute_blake3_response",
      id,
      blake3: hash,
    });
  } catch (err) {
    log.error(TAG, `blake3 computation failed:`, err);
    await sendRawResponse(stream, {
      type: "compute_blake3_response",
      id,
      blake3: null,
      error: err instanceof Error ? err.message : "blake3 computation failed",
    });
  }
}

// ---- ensure_blob support --------------------------------------------------
// exported for use with reliquary's createEnsureBlobHandler

/**
 * check if a blob (by blake3 hash) is available for serving to a peer.
 * this implements the full ensure logic: tauri short-circuit, MemStore
 * fast path, persistent-store fast path, cached-bao import, streaming
 * import for large blobs, and buffered import with bao caching.
 *
 * used as the `hasBlob` callback for `createEnsureBlobHandler`.
 */
export async function ensureBlobAvailable(blake3Hash: string): Promise<boolean> {
  // tauri short-circuit: import the blob's bytes from rust `blobz` into
  // the iroh-blobs FsStore (rust-side, registered on the same endpoint via
  // `StreamRegistry::start_with_blobs`). once it returns `available: true`
  // the requester can pull bytes from us over `iroh-blobs/4` directly —
  // chunked, verified, no size cap. the JS-side MemStore code below never
  // runs in tauri mode (we have no midden in the webview).
  try {
    if (isTauriMode()) {
      let available = false;
      try {
        const result = await dispatch("blob_iroh_ensure", { blake3: blake3Hash });
        available = result?.available === true;
        if (!available) {
          log.debug(
            TAG,
            `tauri ensure_blob ${blake3Hash.slice(0, 16)}... → missing (${result?.reason ?? "unknown"})`
          );
        } else {
          log.debug(TAG, `tauri ensure_blob ${blake3Hash.slice(0, 16)}... → loaded into FsStore`);
        }
      } catch (err) {
        log.warn(TAG, "blob_iroh_ensure dispatch failed:", err);
      }
      return available;
    }
  } catch (err) {
    log.warn(TAG, "tauri ensure_blob short-circuit failed, falling through:", err);
  }

  try {
    // check if already in MemStore (avoids expensive OPFS read + bao recomputation)
    const node = await getMiddenNodeFromIdentity();
    if (typeof (node as any).has_active_blob === "function") {
      // NOTE: awaited — on the worker-hosted node this returns a Promise,
      // and a bare truthiness check on a Promise is always true (which
      // would silently skip every re-import).
      const alreadyLoaded = await (node as any).has_active_blob(blake3Hash);
      if (alreadyLoaded) {
        log.debug(
          TAG,
          `blob ${blake3Hash.slice(0, 16)}... already in MemStore, skipping re-import`
        );
        return true;
      }
    }

    // persistent-store fast path: with the opfs blob store, a previously
    // imported/downloaded blob survives reloads INSIDE the store — no
    // re-import (and no bao recomputation) needed at all.
    if (typeof (node as any).has_complete_blob === "function") {
      const inStore = await (node as any).has_complete_blob(blake3Hash);
      if (inStore) {
        log.debug(
          TAG,
          `blob ${blake3Hash.slice(0, 16)}... complete in persistent store, skipping re-import`
        );
        return true;
      }
    }

    // try fast path: import from cached bao data (skips bao tree recomputation).
    // the bao cache is populated by handleComputeBlake3 / import_blob_and_export_bao.
    const store = blobStore;
    if (typeof (node as any).import_bao === "function") {
      const baoData = await store.getBaoData(blake3Hash);
      if (baoData) {
        try {
          await (node as any).import_bao(blake3Hash, new Uint8Array(baoData));
          log.debug(TAG, `ensured blob ${blake3Hash.slice(0, 16)}... via cached bao (fast path)`);
          return true;
        } catch (baoErr) {
          // bao import failed — fall through to full import path
          log.warn(TAG, `bao import failed, falling back to full import:`, baoErr);
        }
      }
    }

    // slow path: look up raw blob data by blake3 hash, import via add_bytes
    const record = await store.getBlobRecordByBlake3(blake3Hash);

    if (!record) {
      log.debug(TAG, `blob ${blake3Hash.slice(0, 16)}... not found locally`);
      return false;
    }

    // streaming import for large blobs: chunk the OPFS file straight into
    // the store via ImportSession — the whole payload never crosses the
    // wasm boundary as one buffer. (no bao caching on this path; the bao
    // export is a whole-buffer operation and large blobs are exactly where
    // that hurts.)
    if (
      typeof (node as any).start_import === "function" &&
      (record.size ?? 0) >= STREAM_IMPORT_THRESHOLD
    ) {
      const file = await store.getBlobFile(record.blob_id);
      if (file) {
        try {
          const hash = await importBlobStreaming(node, file);
          log.debug(
            TAG,
            `ensured blob ${blake3Hash.slice(0, 16)}... via streaming import (${file.size} bytes)`
          );
          if (hash !== blake3Hash) {
            log.warn(
              TAG,
              `streaming import hash mismatch: expected ${blake3Hash.slice(0, 16)}..., got ${hash.slice(0, 16)}...`
            );
          }
          return true;
        } catch (err) {
          log.warn(TAG, "streaming ensure_blob import failed, falling back to buffered:", err);
        }
      }
    }

    const data = await store.getBlobData(record.blob_id);
    if (!data) {
      log.debug(TAG, `blob ${blake3Hash.slice(0, 16)}... data not found in OPFS`);
      return false;
    }

    // import into MemStore. try the bao-exporting variant so we cache the bao
    // data for next time (if the method is available).
    if (typeof (node as any).import_blob_and_export_bao === "function") {
      try {
        const result = await (node as any).import_blob_and_export_bao(new Uint8Array(data));
        if (result && result.bao) {
          await store.storeBaoData(blake3Hash, result.bao.buffer);
          log.debug(
            TAG,
            `ensured blob ${blake3Hash.slice(0, 16)}... and cached bao (${result.bao.byteLength} bytes)`
          );
        }
      } catch (baoErr) {
        log.warn(TAG, `bao export failed, falling back to plain import:`, baoErr);
        await (node as any).import_blob(new Uint8Array(data));
      }
    } else {
      await (node as any).import_blob(new Uint8Array(data));
    }

    log.debug(TAG, `ensured blob ${blake3Hash.slice(0, 16)}... in MemStore`);
    return true;
  } catch (err) {
    log.error(TAG, `ensure_blob failed:`, err);
    return false;
  }
}

// ---- main entry point -----------------------------------------------------

async function handleStreamAsync(stream: BiStreamLike): Promise<void> {
  const peerId = stream.peer_node_id().slice(0, 16);

  // read the full request message using raw framing (no length prefix).
  // grimoire and midden both send raw JSON terminated by finish().
  let raw: Uint8Array | null;
  if (stream.read_to_end) {
    const result = await stream.read_to_end(10 * 1024 * 1024);
    raw = result.byteLength > 0 ? result : null;
  } else {
    // fallback for streams that don't support raw read
    raw = await stream.read_message();
  }
  if (!raw) {
    // stream closed before any message — nothing to do
    log.debug(TAG, `stream from ${peerId}... closed before message`);
    return;
  }

  const json = decoder.decode(raw);
  let msg: PeerMessage;
  try {
    msg = JSON.parse(json) as PeerMessage;
  } catch (err) {
    log.error(TAG, `failed to parse message from ${peerId}...:`, err);
    return;
  }

  switch (msg.type) {
    case "proxy_request":
      await handleProxyRequest(stream, msg as ProxyRequest);
      break;

    case "compute_blake3_request":
      await handleComputeBlake3(stream, msg as ComputeBlake3Request);
      break;

    default:
      log.debug(TAG, `unhandled message type "${msg.type}" from ${peerId}...`);
      // try to send a generic error response if the message has an id
      if ("id" in msg && typeof msg.id === "number") {
        await sendRawResponse(stream, {
          type: "proxy_response",
          id: msg.id,
          status: 400,
          body: JSON.stringify({
            success: false,
            message: `unsupported message type: ${msg.type}`,
          }),
        });
      }
      break;
  }
}

/**
 * handle an incoming skein/1 ALPN stream.
 *
 * this is the entry point registered with `adapter.registerAlpnHandler("skein/1", ...)`.
 * it fires-and-forgets an async handler — errors are caught and logged internally.
 */
export function handleSkeinStream(stream: BiStreamLike): void {
  handleStreamAsync(stream)
    .catch((err) => {
      log.error(TAG, "stream handler error:", err);
    })
    .finally(() => {
      try {
        stream.close();
      } catch {
        // ignore close errors — stream may already be closed
      }
    });
}

/**
 * create a handler for incoming freqhole/1 ensure-blob streams.
 *
 * this is the entry point registered with `adapter.registerAlpnHandler(DEFAULT_ENSURE_ALPN, ...)`.
 * wraps `ensureBlobAvailable` with reliquary's protocol framing.
 */
export function createSkeinEnsureBlobHandler(): (stream: BiStreamLike) => void {
  return createEnsureBlobHandler({ hasBlob: ensureBlobAvailable });
}
